'use client'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { MICROSITE_PATH, SITE_URL } from '@/utils/siteUrl'
import Link from 'next/link'
import QRCodeLib from 'qrcode'
import { useEffect, useState } from 'react'

const examplesData = [
  {
    id: 'kz5bNVrHJl',
    emoji: '🏊‍♂️',
    title: 'Pool',
    description: 'Safety rules and hours',
    content: [
      'Pool hours: 6 AM - 10 PM',
      'Safety rules and guidelines',
      'Emergency contact: (555) 123-4567',
      'Pool maintenance schedule',
    ],
    dark: '#1e40af',
    light: '#dbeafe',
  },
  {
    id: 'BpGRoul4WU',
    emoji: '🍽️',
    title: 'Kitchen',
    description: 'Usage instructions',
    content: [
      'Coffee machine setup guide',
      'Dishwasher operating instructions',
      'Oven temperature settings',
      'Troubleshooting tips',
    ],
    dark: '#dc2626',
    light: '#fecaca',
  },
  {
    id: 'jAZDmytlOB',
    emoji: '📺',
    title: 'Entertainment Center',
    description: 'Setup and streaming',
    content: [
      'TV and sound system guide',
      'WiFi password: GuestWiFi123',
      'Streaming service logins',
      'Remote control instructions',
    ],
    dark: '#7c3aed',
    light: '#e9d5ff',
  },
  {
    id: 'hI3vT0HlUu',
    emoji: '🚪',
    title: 'Entry & Access',
    description: 'Check-in information',
    content: [
      'Door code: 1234#',
      'Check-out instructions',
      'Parking information',
      'House rules and policies',
    ],
    dark: '#059669',
    light: '#a7f3d0',
  },
  {
    id: 'V4vxBwIHDc',
    emoji: '🛠️',
    title: 'Maintenance & Support',
    description: 'Help when needed',
    content: [
      '24/7 support hotline',
      'Report maintenance issues',
      'Emergency procedures',
      'Local service contacts',
    ],
    dark: '#ea580c',
    light: '#fed7aa',
  },
  {
    id: 'EwKHO_84aX',
    emoji: '🏖️',
    title: 'Recommendations',
    description: 'Area highlights',
    content: [
      'Best local restaurants',
      'Nearby attractions & activities',
      'Transportation options',
      'Weather and beach conditions',
    ],
    dark: '#0891b2',
    light: '#a5f3fc',
  },
]

export function ExamplesSection() {
  const [qrCodes, setQrCodes] = useState<{ [key: string]: string }>({})
  const [hoveredCard, setHoveredCard] = useState<string | null>(null)

  useEffect(() => {
    // Generate QR codes for all microsite URLs
    const generateQRCodes = async () => {
      const codes: { [key: string]: string } = {}

      for (const item of examplesData) {
        const url = `${SITE_URL}${MICROSITE_PATH}/${item.id}`
        try {
          const qrDataUrl = await QRCodeLib.toDataURL(url, {
            width: 200,
            margin: 2,
            color: {
              dark: item.dark,
              light: item.light,
            },
          })
          codes[item.id] = qrDataUrl
        } catch (error) {
          console.error('Error generating QR code for', url, error)
        }
      }

      setQrCodes(codes)
    }

    generateQRCodes()
  }, [])

  return (
    // biome-ignore lint/correctness/useUniqueElementIds: <false positive>
    <section id="examples" className="py-20 px-4 bg-gray-50">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center space-y-4 mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold">Perfect for Every Space</h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            See how others are using ScanReach to create better experiences for their customers
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {examplesData.map((item) => (
            // biome-ignore lint/a11y/noStaticElementInteractions: <we need it>
            <div
              key={item.id}
              className="relative"
              onMouseEnter={() => setHoveredCard(item.id)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <Link href={`${MICROSITE_PATH}/${item.id}`} target="_blank">
                <Card className="hover:shadow-lg transition-shadow h-full">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      {item.emoji} {item.title}
                    </CardTitle>
                    <CardDescription>{item.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-sm font-medium">QR Code Content:</p>
                      <ul className="text-sm text-muted-foreground mt-1 space-y-1">
                        {item.content.map((contentItem) => (
                          <li key={`${contentItem}-${item.id}`}>• {contentItem}</li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </Link>

              {/* QR Code Overlay */}
              {hoveredCard === item.id && qrCodes[item.id] && (
                <Link href={`${MICROSITE_PATH}/${item.id}`} target="_blank">
                  <div className="absolute inset-0 bg-white/95 backdrop-blur-sm rounded-lg flex items-center justify-center transition-all duration-300 animate-in fade-in-0 zoom-in-95 z-10">
                    <div className="text-center space-y-4">
                      {/** biome-ignore lint/performance/noImgElement: <inline image> */}
                      <img
                        src={qrCodes[item.id]}
                        alt={`QR code for ${item.title}`}
                        className="w-32 h-32 mx-auto rounded-lg shadow-lg"
                      />
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-gray-900">Scan to visit</p>
                        <p className="text-xs text-gray-600 break-all px-4">
                          {`${SITE_URL}${MICROSITE_PATH}/${item.id}`}
                        </p>
                      </div>
                    </div>
                  </div>
                </Link>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
